import React, { useEffect, useRef, useState } from 'react';
import { InputInfo, ModelDefinitionDto, OutcomeInfo } from '../../models/APIModels';
import { Alert, Box, Modal, CircularProgress, Grid, Tooltip } from '@mui/material';
import useScreenWidth from '../../hooks/useScreenWidth';
import { useAuth0 } from '@auth0/auth0-react';
import { FormSection, InputProps } from '../../models/type';
import { cloneDeep } from 'lodash';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ApiHelper } from '../../common/ApiHelper';
import { v4 as uuidv4 } from 'uuid';
import { config } from '../../config';
import { toast } from 'react-toastify';
import { getInputs } from '../../Utils/getFormInputs';
import Form from '../Form/Form';
import { LocalStorageService } from '../../common';
import useAppUtils from '../../Utils/useAppUtils';
import Loader from '../Loader';
import { uploadFile } from '../../common/Upload';
import clientUtils from '../../Utils/clientUtils';

import { InferenceStatus, InterfaceTypes } from '../../enums';
import ChatBotInterface from '../ChatBotInterface/ChatBotInterface';
import inferenceHelper from './inferenceHelper';
import Progress from '../Loaders/Progress';
import ShowLogMessage from './ShowLogMessage';
// import { TbLogs } from "react-icons/tb";
import { SiLogstash } from 'react-icons/si';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import Button from '../Button';

interface IProps {
  model: ModelDefinitionDto;
}

const Inference = ({ model }: IProps) => {
  // ➡️ State Variables .....

  const [inputs, setInputs] = useState<InputInfo[]>(model?.aiEngineConfig?.inputs);
  const [forms, setForms] = useState();
  const [inputForm, setInputForm] = useState();
  const [outComesForm, setOutComesForm] = useState();
  const [logVisible, setLogVisible] = useState(false);
  const [inputFormData, setInputFormData] = useState();
  const widthSize = useScreenWidth();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [isLoading, setIsLoading] = useState(false);
  const [contextId, setContextId] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [slowPrediction, setSlowPrediction] = useState(model.longRunningInference);
  const [isLongPredictionRunning, setIsLongPredictionRunning] = useState(false);
  const predictStatucInterval = useRef(null);
  const [errorMessages, setErrorMessages] = useState();
  const [logMessages, setlogMessages] = useState('');
  const [openTooltip, setOpenTooltip] = useState(false);
  const { alert, confirm } = useAppUtils();
  const [timer, setTimer] = useState(0);
  const [variableDisplayOptions, setVariableDisplayOptions] = useState(model.variableDisplayOptions);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const timeCeiling = 600; //maximum minutes is 10
  const [predictInitialized, setPredictInitialized] = useState(false);

  const formComponentRef = useRef();
  const requestIdRef = useRef(null);
  const inputFormDataRef = useRef(null);
  const inputFormFileDataRef = useRef({});
  const isInitializeRef = useRef(false);
  const [uploadingInProgress, setUploadingInProgress] = useState(false);

  const [TotalChatbotCost, setTotalChatbotCost] = useState(0);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '600px',
    maxHeight: '900px',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    border: '1px solid #000',
    boxShadow: 24,
    p: 4,
  };

  // ➡️ Functions/Methods .....

  const handleTooltip = () => {
    setOpenTooltip(!openTooltip);
  };

  const getGridStyle = (column: string) => {
    const smallScreen = 900;
    let style = {};
    if (widthSize && widthSize > smallScreen) {
      if (column === 'left') {
        style = {
          paddingLeft: '10px',
        };
      } else if (column === 'right') {
        style = {
          paddingLeft: '10px',
          borderStyle: 'dotted',
          borderColor: 'rgba(0, 0, 0, 0.12)',
          borderWidth: '0 0 0 2px',
        };
      }
    }
    return style;
  };

  const copyCurlReq = () => {
    const url = `${config.apiBaseUrl}api/predict/${model.modelId}`;
    const inputs = {};
    const outputArr = model.aiEngineConfig.outcomes.map((item: any) => item.name);
    model.aiEngineConfig.inputs.forEach((item: any) => {
      let value;
      switch (item.inputType) {
        case 'Integer':
          value = 0;
          break;
        case 'String':
          value = 'value';
          break;
        case 'Boolean':
          value = false;
          break;
        case 'Float':
          value = 0.0;
          break;
        case 'FloatArray':
          value = [0];
          break;
        default:
          break;
      }
      inputs[item.name] = value;
    });
    const curlReq = `curl --request POST \ --url ${url} \ --header 'Authorization: Bearer APIKEY@{your api key}' \ --header 'Content-type: application/json' \ --data '{"RequestId": null,"InputValues": ${JSON.stringify(
      inputs
    )},"desiredOutcomes":${JSON.stringify(outputArr)}}'`;
    navigator.clipboard.writeText(curlReq);
    toast('API call for this model has been copied to Clipboard as a Curl command!', {
      position: toast.POSITION.TOP_CENTER,
      type: 'success',
      theme: 'colored',
    });
  };

  const onPredict = async () => {
    // console.log({ inputForm: inputForm, outComesForm });
    // handleSubmit(inputFormData);
    if (!isAuthenticated) {
      return loginWithRedirect();
    }
    if (formComponentRef.current) {
      formComponentRef.current.onPredictSubmit();
    }
  };

  /**
   * 
   * @param data 
   - Fast
    - /predict/modelId
   - Fast + File Upload
    - Initialize
    - /predict/modeId/requestId
  - Slow 
    - /predict/modelId
    - /status check
  - Slow + File Upload
    - Initialize
    - /predict/modeId/requestId
    - /status check
   * @returns 
   */
  const onSubmitInput = async (data: unknown) => {
    // console.log('onSubmit', data);
    if (!data) {
      return;
    }
    setErrorMessages('');
    setlogMessages('');
    const newInputForm = cloneDeep(inputForm);
    const newOutcomeForm = cloneDeep(outComesForm);
    resetOutput();
    newInputForm.initialValues = inputFormData;
    isInitializeRef.current = false;
    if (slowPrediction) {
      setIsLongPredictionRunning(true);
    } else {
      setIsLoading(true);
    }
    requestIdRef.current = null;
    const desiredOutcomes = model?.aiEngineConfig?.outcomes ? model?.aiEngineConfig?.outcomes.map((o) => o.name) : [];
    try {
      let payload = {
        RequestId: requestIdRef.current,
        InputValues: inputFormData,
        DesiredOutcomes: desiredOutcomes,
        contextId: contextId,
      };
      const fileObject = inputFormFileDataRef.current;
      if (fileObject) {
        for (const [key, value] of Object.entries(fileObject)) {
          if (payload && payload.InputValues && payload.InputValues.hasOwnProperty(key)) {
            const file = value;
            await initializeInference();
            if (requestIdRef && requestIdRef.current) {
              const reqId = requestIdRef.current;
              setUploadingInProgress(true);
              const { fileName, uploadLink } = await getUploadLinkForInference(reqId, file.name);
              payload.InputValues[key] = fileName;
              await uploadFile(file, uploadLink);
              setUploadingInProgress(false);
            }
          }
        }
      }
      const reqId = requestIdRef.current;
      const isInitialized = isInitializeRef.current;
      if (reqId) {
        // payload.InputValues = inputFormDataForFileRef.current;
        payload.RequestId = reqId;
      }
      if (!slowPrediction && !isInitialized) {
        const response = await ApiHelper.Predict(payload, model.modelId);
        updateLog(response.data.logMessages, response.data.errorMessages);
        await populateResults(response);
      } else if (!slowPrediction && reqId && isInitialized) {
        const response = await ApiHelper.PredictWithRequstId(payload, model.modelId, reqId);
        await populateResults(response);
      } else if (reqId && slowPrediction && isInitialized) {
        const predictWithReq = await ApiHelper.PredictWithRequstId(payload, model.modelId, reqId);
        if (predictWithReq && predictWithReq.data && !predictWithReq.data.errorMessages) {
          requestIdRef.current = predictWithReq.data.requestId;
          predictStatucInterval.current = setInterval(checkPredicStatus, 5000);
        }
      } else if (!reqId && slowPrediction && !isInitialized) {
        const response = await ApiHelper.Predict(payload, model.modelId);
        updateLog(response.data.logMessages, response.data.errorMessages);
        if (response && response.data && !response.data.errorMessages) {
          requestIdRef.current = response.data.requestId;
          predictStatucInterval.current = setInterval(checkPredicStatus, 5000);
        }
      }
    } catch (e) {
      console.error('Unable to predict due to', e);
      alert('Bad Inputs', 'error');
      setIsLoading(false);
    }
  };

  const onFormChange = async (data: unknown) => {
    // console.log('onFormChanged', data);
    setInputFormData(data);
    inputFormDataRef.current = data;
  };

  // 1. Initialize Inference with Model ID and get the requestId
  // 2. Get File Upload link with requestId and take uploadLink url
  // 3. Upload File
  // 4. Status check predict endpoint with requestId
  const initializeInference = async () => {
    try {
      if (!requestIdRef.current) {
        isInitializeRef.current = true;
        const { data, status } = await ApiHelper.InitializeInference(model.modelId);
        if (status === 200) {
          const { requestId, contextId } = data;
          if (model.needsContext) {
            setContextId(contextId);
          }
          setPredictInitialized(true);
          requestIdRef.current = requestId;
        }
      }
    } catch (error) {
      console.error('Unable to initialize inference', { error });
    }
  };
  const getUploadLinkForInference = async (reqId, fileName) => {
    try {
      const uploadLinkRes = await ApiHelper.GetUploadLinkForInference(reqId, fileName);
      if (uploadLinkRes && uploadLinkRes.data) {
        return uploadLinkRes.data;
      }
      alert('Unable to get upload link', 'error');
      clearStatusCheck();
      return null;
    } catch (error) {
      console.error('Unable to get upload link', { error });
      return null;
    }
  };
  const onFileChange = async (type: string, file: File, name: string) => {
    if (isLongPredictionRunning) {
      return;
    }
    inputFormFileDataRef.current[name] = file;
  };

  const downloadOutcomeFile = async (type: string, file: File, name: string) => {
    // console.log('onDownload', { name, outComesForm });
    try {
      const reqId = requestIdRef.current;
      if (outComesForm && reqId) {
        alert('Downloading...');
        const outcome = outComesForm.inputs.find((input) => input.name === name);
        const fileName = outcome ? outcome.orginalValue : '';
        const { data, status, request } = await ApiHelper.GetDownloadLinkForInference(reqId, fileName);
        if (status === 200) {
          await downloadTxtFile(request.responseURL, 'outcome', fileName);
        }
      }
    } catch (error) {
      alert('Unable to download', 'error');
    }
  };
  const downloadTxtFile = async (url, id, fileName) => {
    if (url) {
      const response = await fetch(url);
      if (response) {
        const blob = await response.blob();
        if (blob) {
          const fileUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = fileName;
          link.href = fileUrl;
          link.click();
        }
      }
    }
  };
  const checkPredicStatus = async () => {
    // console.log('checkPredicStatus', timer);
    const reqId = requestIdRef.current;
    if (reqId) {
      const response = await ApiHelper.PredictStatusCheck(reqId);
      if (model?.permissions && model.permissions.includes('Write') && response && response.data && response.data.logMessages) {
        setlogMessages(response.data.logMessages);
      }
      if (response.data && response.data.status === InferenceStatus.Processing) {
        setTimer((timer) => timer + 1);
      } else if (response.data.outcomes && response.data.status === InferenceStatus.Completed) {
        clearStatusCheck();
        setIsLongPredictionRunning(false);
        await populateResults(response);
      } else if (response && response.data && response.data.status === InferenceStatus.Error) {
        requestIdRef.current = null;
        setErrorMessages(response.data.errorMessages ? response.data.errorMessages : 'Unexpected Error!');
        clearStatusCheck();
      }
    }
  };
  useEffect(() => {
    if (timer < timeCeiling) {
      setMinutes(Math.floor(timer / 60));
      setSeconds(timer % 60);
    } else {
      clearStatusCheck();
    }
  }, [timer]);

  const populateResults = async (response) => {
    const newOutcomeForm = cloneDeep(outComesForm);
    const outcomes = newOutcomeForm.inputs;
    let newOutComes: InputProps[] = [];
    let outcomeInputs = [];
    let fileName = null;
    let imagePreviewEnabled = false;
    if (response && response.data && response.data.status === InferenceStatus.Completed) {
      requestIdRef.current = requestIdRef.current ? requestIdRef.current : response.data.requestId; 
      if (model.needsContext) {
        setContextId(response.data.contextId);
      }
      if (response.data.outcomes && !clientUtils.isObjectEmpty(response.data.outcomes)) {
        const outcomesDict = response.data.outcomes;
        newOutcomeForm.initialValues = {
          ...newOutcomeForm.initialValues,
        };
        for (const key in outcomesDict) {
          let items = outcomesDict[key];
          items = items.map((item) => {
            let newInput: InputProps = { ...item };
            newInput.description = '';
            newInput.name = key;
            newInput.placeholder = `Enter ${key}`;
            newInput.label = key;
            if (item.dataType === 'File') {
              newInput.type = 'file';
              newInput.typeValue = 'string';
              newInput.inputAction = 'Download';
              newInput.placeholder = '';
              newInput.value = '';
              if (!clientUtils.isObjectEmpty(variableDisplayOptions) && newInput && requestIdRef.current) {
                const keyName = clientUtils.uniqueKeyNameGenForVariableDisplayOptions(key, 'outputcon');
                newInput.imagePreviewEnabled = variableDisplayOptions.hasOwnProperty(keyName)
                  ? variableDisplayOptions[keyName].enablePictureViewer
                  : false;
                if (newInput.imagePreviewEnabled) {
                  imagePreviewEnabled = true;
                  fileName = item.value;
                }
              }
            } else if (item.dataType === 'String') {
              newInput.type = 'text';
              newInput.typeValue = 'string';
            }
            newInput.value = item.probability > 0 ? `${item.value} (Probabilty: ${item.probability})` : item.value;
            newInput.orginalValue = item.value;
            newInput.disabled = true;
            newInput.options = [];
            return newInput;
          });

          newOutComes = newOutComes.concat(items);
        }
        newOutcomeForm.inputs = newOutComes;

        for (let i = 0; i < newOutComes.length; i++) {
          newOutcomeForm.initialValues[newOutComes[i].name] = newOutComes[i].value;
        }
        if (imagePreviewEnabled && requestIdRef.current) {
          try {
            const { data, status, request } = await ApiHelper.GetDownloadLinkForInference(requestIdRef.current, fileName);
            if (status === 200 && request.responseURL) {
              const index = newOutcomeForm.inputs.findIndex((input) => input.orginalValue === fileName);
              if (index > -1) {
                newOutcomeForm.inputs[index].imageUrl = request.responseURL;
              }
            }
          } catch (e) {}
        }
        setOutComesForm(() => newOutcomeForm);
      }
    } else if (response && response.data && response.data.status === InferenceStatus.Error) {
      setErrorMessages(response.data.errorMessages);
    }
    setIsLoading(false);
  };

  const resetOutput = () => {
    const newOutcomeForm = cloneDeep(outComesForm);
    const outcomes = newOutcomeForm.inputs;
    for (let i = 0; i < outcomes.length; i++) {
      newOutcomeForm.inputs[i].value = '';
      newOutcomeForm.initialValues[outcomes[i].name] = '';
    }
    setOutComesForm(newOutcomeForm);
  };

  const clearStatusCheck = () => {
    if (predictStatucInterval.current) {
      // console.log('clear interval');
      clearInterval(predictStatucInterval.current);
    }
    setIsLongPredictionRunning(false);
    setIsLoading(false);
    setUploadingInProgress(false);
    LocalStorageService.removeItem('longRunningInference');
    setMinutes(10);
    setSeconds(0);
  };

  const copyEmbedCode = async () => {
    const IframeCode = inferenceHelper.chatBotIframeGenerator(model.modelId);
    await clientUtils.copyToClipboard(IframeCode);
    toast('Embed Code Copied as Iframe!', {
      position: toast.POSITION.TOP_RIGHT,
      type: 'success',
      theme: 'colored',
    });
  };

  // ▶️ Hooks .....

  useEffect(() => {
    const predictEngineType = model?.aiEngineConfig?.predictEngineType;
    const forms: { [K in FormSection]: InputProps[] } = {
      Input: inferenceHelper.generateInputs(model?.aiEngineConfig?.inputs ?? [], predictEngineType, model.variableDisplayOptions),
      Outcomes: inferenceHelper.generateOutcomes(model?.aiEngineConfig?.outcomes ?? []),
    };
    setInputs(model?.aiEngineConfig?.inputs ?? []);
    setForms(forms);
    setInputForm(getInputs<InputInfo>('Input', forms));
    setOutComesForm(getInputs<InputInfo>('Outcomes', forms));
    setSlowPrediction(model.longRunningInference);
  }, [model]);

  useEffect(() => {
    return () => {
      clearStatusCheck();
    };
  }, []);

  const closeLogMessage = (type: string) => {
    if (type === 'Error') {
      setErrorMessages(null);
    } else {
      setlogMessages(null);
    }
  };
  const updateLog = (logMessages = '', errorMessages = '') => {
    setlogMessages(logMessages);
    setErrorMessages(errorMessages);
  };

  return (
    <>
      {model && (
        <Grid container item>
          {isLoading && <Progress progressLabel="Please Wait ..." />}

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            style={getGridStyle('left')}
            sx={{ paddingLeft: '2px !important', paddingRight: '4px' }}
          >
            {model.interfaceType === InterfaceTypes.Chatbot ? (
              <ChatBotInterface setTotalChatbotCost={setTotalChatbotCost} model={model} updateLog={updateLog} />
            ) : (
              <>
                {inputForm && (
                  <Form
                    {...inputForm}
                    initialValues={inputForm?.initialValues}
                    titleForm="Inputs"
                    onSubmit={onSubmitInput}
                    labelButtonSubmit="Predict"
                    onFormChange={onFormChange}
                    showButtons={false}
                    ref={formComponentRef}
                    onInputActionFun={onFileChange}
                    uploadingInProgress={uploadingInProgress}
                  />
                )}
              </>
            )}
          </Grid>

          <Grid item xs={12} sm={12} md={6} style={getGridStyle('right')}>
            <Box className="hedading">
              <h4 className="title">Inference</h4>
              <div className="line"></div>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Disabled predict button if chatbot enabled */}
              {!isLongPredictionRunning && model.interfaceType === InterfaceTypes.Form && (
                <Button
                  size="small"
                  sx={{ width: '25%', height: '35px', marginRight: '5px' }}
                  variant="contained"
                  onClick={onPredict}
                  disabled={inputs.length === 0 || uploadingInProgress}
                >
                  Predict
                </Button>
              )}
              {isLongPredictionRunning && model.interfaceType === InterfaceTypes.Form && (
                <Button
                  size="small"
                  sx={{ width: '25%', height: '35px', marginRight: '5px' }}
                  variant="contained"
                  disabled={isLongPredictionRunning}
                >
                  Predict{' '}
                  <CircularProgress
                    size={20}
                    sx={{
                      color: 'green',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                </Button>
              )}

              <div style={{ marginLeft: '0px', marginTop: '13px', display: 'flex' }}>
                <p className="author">
                  {model.interfaceType === InterfaceTypes.Chatbot ? 'Chatbot Cost' : 'Estimated Calculation Cost'} ${' '}
                  {model.totalCost.toFixed(6)}
                </p>
                <div style={{ marginLeft: '5px', marginTop: '2px' }}>
                  <Tooltip
                    arrow
                    placement="top"
                    open={openTooltip}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: '500px',
                        },
                      },
                    }}
                    title={
                      <>
                        <div>This is an estimated compute cost for inference (calculated from average across all users).</div>
                        <div> The model's author has listed the model pricing information as "{model.pricingInfoToDisplay}".</div>
                        <div>Note: exact compute cost is calculated at the time of inference and charged to the account.</div>
                      </>
                    }
                  >
                    <i className="nc-icon nc-alert-circle-i" onClick={() => handleTooltip()}></i>
                  </Tooltip>
                </div>
              </div>

              <Tooltip title="Copy as Curl">
                <ContentCopyIcon onClick={copyCurlReq} className="copyButton" fontSize="small" />
              </Tooltip>
              {model?.permissions?.includes('Write') && (
                <Tooltip title="Show Logs">
                  <ReportProblemOutlinedIcon onClick={() => setLogVisible(true)} className="copyButton" fontSize="small" />
                </Tooltip>
              )}
            </Box>

            {/* ## Chatbot total Cost Showing ## */}
            {model.interfaceType === InterfaceTypes.Chatbot && (
              <>
                <p className="author">Chatbot Total Cost $ {TotalChatbotCost.toFixed(6)}</p>
              </>
            )}

            {/* ## Copy Embed Code ## */}

            <Box>
              {model.interfaceType === InterfaceTypes.Chatbot && (
                <Button
                  variant="contained"
                  onClick={copyEmbedCode}
                  endIcon={<ContentCopyIcon className="copyButton" fontSize="small" />}
                >
                  Copy Embed Code
                </Button>
              )}
            </Box>

            {/* ## Showing output options based on Chatbot options */}
            {model.interfaceType === InterfaceTypes.Form && (
              <Box sx={{ marginTop: '20px' }}>
                {outComesForm && (
                  <Form
                    {...outComesForm}
                    initialValues={outComesForm?.initialValues}
                    titleForm="Outcomes"
                    onSubmit={onSubmitInput}
                    labelButtonSubmit=""
                    showButtons={false}
                    onInputActionFun={downloadOutcomeFile}
                  />
                )}
              </Box>
            )}

            {/* {outComeValues.map((o) => (
                      <strong key={o.probability}>Probability : {o.probability}</strong>
                    ))} */}

            {logVisible && (
              <ShowLogMessage
                open={logVisible}
                onClose={() => setLogVisible(false)}
                logMessages={logMessages}
                errorMessages={errorMessages}
                closeLogMessage={closeLogMessage}
              />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default Inference;
